import React from "react";
import { useSelector } from "react-redux";
import { updateDocInDb } from "../../../../app/firestore/firestoreService";

export default function DocFillValidationArrows({ validationIssues, pageIndex }) {
  const { pageScaleFill } = useSelector((state) => state.annot);
  const { doc } = useSelector((state) => state.doc);
  const pageScale = pageScaleFill;

  // Filter issues for this page
  const pageIssues = validationIssues.filter(issue => issue.field.page === pageIndex);

  if (pageIssues.length === 0) {
    return null;
  }

  // Standard page width (612 points) - position arrows to the right of the document
  const documentWidth = 612 * pageScale;
  const arrowStartX = documentWidth + 6; // 20px margin from document edge

  return (
    <>
      {pageIssues.map((issue, index) => {
        const field = issue.field;

        // Position arrows vertically aligned with the field, but on the right side of document
        const arrowTop = (field.top + field.height / 2 - 10) * pageScale;

        // Stack multiple arrows vertically if they would overlap
        const verticalOffset = index * 35; // 35px spacing between arrows
        const finalArrowTop = arrowTop + verticalOffset;

        // Determine colors based on highlight flag
        const isHighlighted = issue.highlight;
        const backgroundColor = isHighlighted ? "rgba(255, 165, 0, 0.9)" : "rgba(244, 52, 52, 0.9)"; // Orange or Red
        const arrowColor = isHighlighted ? "rgba(255, 165, 0, 0.9)" : "rgba(244, 52, 52, 0.9)";

        const handleArrowClick = () => {
          // Trigger the same highlighting behavior as "Go to field" button
          window.dispatchEvent(new CustomEvent('docFillValidation', {
            detail: {
              action: 'highlightSingleField',
              field: field,
              highlight: true
            }
          }));
        };

        // Determine if this is an Amount field (should use "0" instead of "n/a")
        const isAmountField = field.name.startsWith("Amount");
        const fillValue = isAmountField ? "0" : "n/a";
        const buttonLabel = isAmountField ? "Enter 0" : "Add n/a";

        const handleAddNA = (e) => {
          e.stopPropagation(); // Prevent arrow click from firing

          // Find the field element
          const fieldElement = document.querySelector(`[name="${field.name}"]`) ||
                              document.getElementById(field.name);

          if (fieldElement) {
            // Properly trigger React's state update by using native value setter and events
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
              fieldElement.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype,
              'value'
            ).set;
            nativeInputValueSetter.call(fieldElement, fillValue);

            // Dispatch input event to update React state
            fieldElement.dispatchEvent(new Event('input', { bubbles: true }));

            // Dispatch blur event to trigger the save to database
            fieldElement.dispatchEvent(new Event('blur', { bubbles: true }));
          } else {
            // Fallback: directly update database if element not found
            updateDocInDb(doc.id, {
              [`formFieldValues.${field.name}`]: fillValue,
            });
          }

          // Trigger validation refresh after a delay to allow state to update
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('docFillValidation', {
              detail: { action: 'refreshValidation' }
            }));
          }, 500);
        };

        const handleHideOnDocument = (e) => {
          e.stopPropagation(); // Prevent arrow click from firing

          // Dispatch event to hide this specific field from validation arrows
          window.dispatchEvent(
            new CustomEvent("docFillValidation", {
              detail: {
                action: "hideFieldValidation",
                field: field,
              },
            })
          );
        };

        // Show quick-fill button only for blank text/textarea fields
        const showAddNA =
          issue.message === "field is blank" &&
          (issue.type === "text" || issue.type === "textarea");

        return (
          <div
            key={`validation-arrow-${index}`}
            className="validation-arrow"
            onClick={handleArrowClick}
            style={{
              position: "absolute",
              left: `${arrowStartX}px`,
              top: `${finalArrowTop}px`,
              zIndex: 1000,
              pointerEvents: "auto",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: backgroundColor,
                color: "white",
                padding: "6px 10px",
                borderRadius: "6px",
                fontSize: "11px",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                boxShadow: "0 3px 6px rgba(0,0,0,0.4)",
                maxWidth: "320px",
                border: isHighlighted ? "2px solid #ff8c00" : "none",
              }}
            >
              <div
                style={{
                  width: 0,
                  height: 0,
                  borderTop: "8px solid transparent",
                  borderBottom: "8px solid transparent",
                  borderRight: `12px solid ${arrowColor}`,
                  marginRight: "6px",
                  marginLeft: "-20px",
                }}
              />
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <div>
                  <div style={{ fontSize: "10px", opacity: 0.9 }}>
                    Page {pageIndex + 1} • {field.name}
                    {isHighlighted && (
                      <span
                        style={{ marginLeft: "5px", fontSize: "9px" }}
                        role="img"
                        aria-label="pointing left"
                      >
                        👈 This field
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "11px", fontWeight: "bold" }}>
                    {issue.message}
                  </div>
                </div>
                <div>
                  {showAddNA && (
                    <button
                      onClick={handleAddNA}
                      style={{
                        backgroundColor: "white",
                        color: "#333",
                        border: "none",
                        borderRadius: "4px",
                        padding: "3px 8px",
                        fontSize: "10px",
                        fontWeight: "bold",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {buttonLabel}
                    </button>
                  )}
                  <br />
                  <button
                    onClick={handleHideOnDocument}
                    style={{
                      backgroundColor: "transparent",
                      color: "white",
                      border: "1px solid rgba(255,255,255,0.5)",
                      borderRadius: "4px",
                      padding: "3px 8px",
                      fontSize: "10px",
                      fontWeight: "bold",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Hide
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
